import { useState, useEffect } from "react";
import { useMutation } from "convex/react";
import { api } from "../../convex/_generated/api";
import { X, Plus, Music, SlidersHorizontal, Mic, Layers } from "lucide-react";
import type { Artist } from "../data";

interface Props {
  artists: Artist[];
  isAdmin?: boolean;
  onClose: () => void;
}

const PROJECT_TYPES = [
  { value: "beat", label: "Beat", Icon: Music, desc: "Lease or exclusive beat" },
  { value: "mix_master", label: "Mix & Master", Icon: SlidersHorizontal, desc: "Mix and master your track" },
  { value: "full_production", label: "Full Production", Icon: Mic, desc: "Full production from scratch" },
  { value: "3_track_bundle", label: "3-Track Bundle", Icon: Layers, desc: "3 tracks — $500 AUD fixed" },
];

const BUNDLE_PRICE_DOLLARS = "500";

interface TrackEntry {
  title: string;
  musicalKey: string;
  notes: string;
}

function emptyTrack(): TrackEntry {
  return { title: "", musicalKey: "", notes: "" };
}

export default function StartProjectModal({ artists, isAdmin = false, onClose }: Props) {
  const createProject = useMutation(api.projectCreation.createProject);

  const [mode, setMode] = useState<"existing" | "new">("existing");
  const [artistId, setArtistId] = useState(artists[0]?.id ?? "");
  const [newArtistName, setNewArtistName] = useState("");
  const [newArtistStageName, setNewArtistStageName] = useState("");
  const [newArtistEmail, setNewArtistEmail] = useState("");
  const [title, setTitle] = useState("");
  const [projectType, setProjectType] = useState("beat");
  const [priceDollars, setPriceDollars] = useState("");
  const [brief, setBrief] = useState("");
  const [bpm, setBpm] = useState("");
  const [musicalKey, setMusicalKey] = useState("");
  const [referenceLinks, setReferenceLinks] = useState("");
  const [tracks, setTracks] = useState<TrackEntry[]>([emptyTrack(), emptyTrack(), emptyTrack()]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (projectType === "3_track_bundle") {
      setPriceDollars(BUNDLE_PRICE_DOLLARS);
    }
  }, [projectType]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");

    if (!title.trim()) {
      setError("Project title is required");
      return;
    }

    const isBundle = projectType === "3_track_bundle";
    const effectivePrice = isBundle ? BUNDLE_PRICE_DOLLARS : priceDollars;

    if (!effectivePrice || parseFloat(effectivePrice) <= 0) {
      setError("Price is required");
      return;
    }

    if (mode === "existing" && !artistId) {
      setError("Select an artist");
      return;
    }

    if (mode === "new" && !newArtistName.trim()) {
      setError("Artist name is required");
      return;
    }

    setSaving(true);
    try {
      const links = referenceLinks
        .split("\n")
        .map((l) => l.trim())
        .filter((l) => l.length > 0);

      const trackPayload = isBundle
        ? tracks.map((t, i) => ({
            title: t.title.trim() || `Track ${i + 1}`,
            status: "in_progress",
            musicalKey: t.musicalKey.trim() || undefined,
            notes: t.notes.trim() || undefined,
          }))
        : undefined;

      await createProject({
        artistId: mode === "existing" ? (artistId as any) : ("placeholder" as any),
        title: title.trim(),
        projectType,
        priceCents: Math.round(parseFloat(effectivePrice) * 100),
        brief: brief.trim() || undefined,
        bpm: bpm ? parseInt(bpm, 10) : undefined,
        musicalKey: musicalKey.trim() || undefined,
        referenceLinks: links.length > 0 ? links : undefined,
        tracks: trackPayload,
        newArtistName: mode === "new" ? newArtistName.trim() : undefined,
        newArtistStageName: newArtistStageName.trim() || undefined,
        newArtistEmail: newArtistEmail.trim() || undefined,
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create project");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Start New Project</h2>
          <button className="modal-close" onClick={onClose} type="button">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form className="modal-form" onSubmit={handleSubmit}>
          <div className="form-section">
            <label>
              <span>Artist</span>
              {isAdmin ? (
                <div className="artist-mode-toggle">
                  <button
                    className={mode === "existing" ? "active" : ""}
                    onClick={() => setMode("existing")}
                    type="button"
                  >
                    Existing
                  </button>
                  <button
                    className={mode === "new" ? "active" : ""}
                    onClick={() => setMode("new")}
                    type="button"
                  >
                    New artist
                  </button>
                </div>
              ) : (
                <p className="form-hint">Project will be created for your account.</p>
              )}
            </label>

            {isAdmin && mode === "existing" ? (
              <label>
                <span>Select artist</span>
                <select
                  onChange={(e) => setArtistId(e.target.value)}
                  value={artistId}
                >
                  {artists.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}

            {isAdmin && mode === "new" ? (
              <>
                <label>
                  <span>Artist name *</span>
                  <input
                    onChange={(e) => setNewArtistName(e.target.value)}
                    placeholder="John Doe"
                    value={newArtistName}
                  />
                </label>
                <label>
                  <span>Stage name (optional)</span>
                  <input
                    onChange={(e) => setNewArtistStageName(e.target.value)}
                    placeholder="JD Beats"
                    value={newArtistStageName}
                  />
                </label>
                <label>
                  <span>Email (optional)</span>
                  <input
                    onChange={(e) => setNewArtistEmail(e.target.value)}
                    placeholder="artist@email.com"
                    type="email"
                    value={newArtistEmail}
                  />
                </label>
              </>
            ) : null}
          </div>

          <div className="form-section">
            <label>
              <span>Project title *</span>
              <input
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Summer Beat Pack"
                value={title}
              />
            </label>

            <div className="form-field">
              <span className="form-field-label">Select project type</span>
              <div className="product-selection-grid">
                {PROJECT_TYPES.map((t) => {
                  const Icon = t.Icon;
                  return (
                    <button
                      className={`product-card${projectType === t.value ? " selected" : ""}`}
                      key={t.value}
                      onClick={() => setProjectType(t.value)}
                      type="button"
                    >
                      <Icon className="product-card-svg" />
                      <span className="product-card-label">{t.label}</span>
                      <span className="product-card-desc">{t.desc}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {projectType !== "3_track_bundle" ? (
              <label>
                <span>Price (AUD) *</span>
                <input
                  min="0"
                  onChange={(e) => setPriceDollars(e.target.value)}
                  placeholder="150"
                  step="0.01"
                  type="number"
                  value={priceDollars}
                />
              </label>
            ) : null}

            <label>
              <span>Brief / notes (optional)</span>
              <textarea
                onChange={(e) => setBrief(e.target.value)}
                placeholder="Project details, requirements, references..."
                rows={3}
                value={brief}
              />
            </label>

            <div className="form-row-two">
              <label>
                <span>BPM (optional)</span>
                <input
                  min="0"
                  onChange={(e) => setBpm(e.target.value)}
                  placeholder="140"
                  type="number"
                  value={bpm}
                />
              </label>

              <label>
                <span>Key (optional)</span>
                <input
                  onChange={(e) => setMusicalKey(e.target.value)}
                  placeholder="C minor"
                  value={musicalKey}
                />
              </label>
            </div>

            <label>
              <span>Reference links (optional, one per line)</span>
              <textarea
                onChange={(e) => setReferenceLinks(e.target.value)}
                placeholder="https://soundcloud.com/..."
                rows={2}
                value={referenceLinks}
              />
            </label>
          </div>

          {projectType === "3_track_bundle" ? (
            <div className="form-section">
              <h3 className="form-section-title">3-Track Bundle</h3>
              {tracks.map((track, i) => (
                <div className="track-entry" key={i}>
                  <p className="track-entry-label">Track {i + 1}</p>
                  <label>
                    <span>Title</span>
                    <input
                      onChange={(e) => {
                        setTracks((prev) => prev.map((t, idx) => idx === i ? { ...t, title: e.target.value } : t));
                      }}
                      placeholder={`Song ${i + 1}`}
                      value={track.title}
                    />
                  </label>
                  <div className="form-row-two">
                    <label>
                      <span>Key (optional)</span>
                      <input
                        onChange={(e) => {
                          setTracks((prev) => prev.map((t, idx) => idx === i ? { ...t, musicalKey: e.target.value } : t));
                        }}
                        placeholder="C"
                        value={track.musicalKey}
                      />
                    </label>
                    <label>
                      <span>Notes (optional)</span>
                      <input
                        onChange={(e) => {
                          setTracks((prev) => prev.map((t, idx) => idx === i ? { ...t, notes: e.target.value } : t));
                        }}
                        placeholder=""
                        value={track.notes}
                      />
                    </label>
                  </div>
                </div>
              ))}
            </div>
          ) : null}

          {error ? <p className="form-error">{error}</p> : null}

          <div className="modal-actions">
            <button className="cancel-button" onClick={onClose} type="button">
              Cancel
            </button>
            <button
              className="primary-action"
              disabled={saving}
              type="submit"
            >
              <Plus className="h-4 w-4" />
              {saving ? "Creating..." : "Create project"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
